import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Ban,
  BookOpen,
  ChartColumn,
  CircleCheck,
  CircleDollarSign,
  ClipboardCheck,
  ExternalLink,
  Plus,
  Radar,
  Save,
  Search,
  ShieldCheck,
  ShoppingCart,
  Tag,
  X
} from "lucide-react";
import {
  createAdminTradeProcurement,
  createAdminTradeSale,
  loadOperatorRadar,
  loadAdminTradeLedger,
  updateAdminTradeProcurement,
  updateAdminTradeSaleStatus
} from "./api";
import { tradePlatforms, translateTradePlatform } from "./tradePlatforms";
import type {
  AdminTradeLedger,
  AdminTradeLedgerPeriodSummary,
  AdminTradeProcurement,
  AdminTradeSale,
  Category,
  Opportunity,
  ShippingMethod,
  TradeLedgerPeriod,
  TradePlatform
} from "./types";
import { LoadingOverlay } from "./LoadingOverlay";
import { PullToRefresh } from "./PullToRefresh";

const currency = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const categories: Array<{ id: Category; label: string }> = [
  { id: "baby", label: "母婴" },
  { id: "mobility", label: "出行" }
];

const procurementCategoryGroups = [
  { label: "儿童出行", options: ["平衡车", "推车"] },
  { label: "骑行", options: ["折叠自行车"] },
  { label: "厨电", options: ["吸油烟机", "燃气灶", "燃气炉"] },
  { label: "手机", options: ["手机"] },
  { label: "大疆", options: ["无人机", "手持云台", "云台相机", "运动相机", "无线麦克风", "图传设备"] },
  { label: "通用", options: ["配件"] }
] as const;

const quickColors = ["黑", "白", "灰", "银", "蓝", "红", "绿", "粉", "米", "棕", "其他"];

function parentCategoryForProduct(value: string): Category {
  return value === "推车" ? "baby" : "mobility";
}

const periodTabs: Array<{ id: TradeLedgerPeriod; label: string }> = [
  { id: "week", label: "周" },
  { id: "month", label: "月" },
  { id: "quarter", label: "季" }
];

type WorkspaceSection = "radar" | "buy" | "sell" | "ledger" | "analytics";

const workspaceTabs = [
  { id: "radar", label: "机会", icon: Radar },
  { id: "buy", label: "采购", icon: ShoppingCart },
  { id: "sell", label: "销售", icon: CircleDollarSign },
  { id: "ledger", label: "账本", icon: BookOpen },
  { id: "analytics", label: "分析", icon: ChartColumn }
] as const;

function toDateTimeLocalValue(value = new Date()) {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toDateInputValue(value: string | null | undefined) {
  const fallback = toDateTimeLocalValue().slice(0, 10);
  if (!value) {
    return fallback;
  }
  const raw = value.trim().replace(" ", "T").split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : toDateTimeLocalValue(parsed).slice(0, 10);
}

function formatProcurementDateTime(value: string | null) {
  if (!value) {
    return "—";
  }
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatProcurementDate(value: string | null) {
  if (!value) {
    return "—";
  }
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatPercent(value: number | null) {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

function formatDays(value: number | null) {
  return value === null ? "—" : `${value.toFixed(1)}天`;
}

function formatTrendAmount(value: number) {
  const amount = Math.abs(value);
  if (amount >= 10000) {
    return `¥${(value / 10000).toFixed(1)}万`;
  }
  if (amount >= 1000) {
    return `¥${(value / 1000).toFixed(1)}k`;
  }
  return `¥${Math.round(value)}`;
}

function categoryLabel(value: Category | string) {
  return categories.find((item) => item.id === value)?.label ?? String(value);
}

const tradeCategoryTones: Record<string, string> = {
  "平衡车": "balance-bike",
  "折叠自行车": "folding-bike",
  "推车": "stroller",
  "吸油烟机": "range-hood",
  "燃气灶": "gas-stove",
  "燃气炉": "gas-cooker",
  "配件": "accessory",
  "手机": "phone",
  "无人机": "drone",
  "手持云台": "gimbal",
  "云台相机": "gimbal-camera",
  "运动相机": "action-camera",
  "无线麦克风": "wireless-mic",
  "图传设备": "video-transmitter"
};

function tradeCategoryClass(category: Category | string, subcategory?: string | null) {
  const key = subcategory?.trim() || String(category).trim();
  return `${category} trade-category-${tradeCategoryTones[key] ?? category}`;
}

function shippingMethodLabel(value: ShippingMethod) {
  return value === "pickup" ? "自提" : "快递";
}

function calculateXianyuPlatformFee(value: string) {
  const salePrice = Number(value);
  return Number.isFinite(salePrice) && salePrice > 0 ? (salePrice * 0.006).toFixed(2) : "0.00";
}

function timeValue(value: string | null | undefined) {
  if (!value) {
    return 0;
  }
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function isWithinLastDays(value: string | null | undefined, days: number) {
  const timestamp = timeValue(value);
  return timestamp > 0 && timestamp >= Date.now() - days * 24 * 60 * 60 * 1000;
}

function saleNetAmount(sale: AdminTradeSale) {
  return (
    Number(sale.sale_price) * Number(sale.quantity) -
    Number(sale.outbound_freight) -
    Number(sale.consumable_cost) -
    Number(sale.platform_fee) -
    Number(sale.misc_cost)
  );
}

function procurementTotalCost(item: AdminTradeProcurement) {
  return (
    Number(item.buy_price) * Number(item.quantity) +
    Number(item.inbound_freight) +
    Number(item.consumable_cost) +
    Number(item.misc_cost)
  );
}

function procurementUnitCost(item: {
  quantity: number;
  buy_price: number;
  inbound_freight: number;
  consumable_cost: number;
  misc_cost: number;
  refurbishment_cost: number;
}) {
  const quantity = Math.max(Number(item.quantity), 1);
  return (
    Number(item.buy_price) * quantity +
    Number(item.inbound_freight) +
    Number(item.consumable_cost) +
    Number(item.misc_cost) +
    Number(item.refurbishment_cost)
  ) / quantity;
}

function useProgressiveItems<T>(items: T[], pageSize = 20) {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [items, pageSize]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  function loadMore() {
    setVisibleCount((current) => Math.min(current + pageSize, items.length));
  }

  return { visibleItems, hasMore, loadMore };
}

export function TradeLedgerPanel({ token, onChanged }: { token: string; onChanged: () => void }) {
  const [section, setSection] = useState<WorkspaceSection>("buy");
  const [ledger, setLedger] = useState<AdminTradeLedger | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "done" | "error">("idle");
  const [radar, setRadar] = useState<Opportunity[]>([]);
  const [radarStatus, setRadarStatus] = useState<"loading" | "ready" | "error">("loading");
  const [buyPlatform, setBuyPlatform] = useState<TradePlatform>("xianyu");
  const [sellPlatform, setSellPlatform] = useState<TradePlatform>("xianyu");
  const [buySubcategory, setBuySubcategory] = useState("平衡车");
  const [buyShippingMethod, setBuyShippingMethod] = useState<ShippingMethod>("express");
  const [sellShippingMethod, setSellShippingMethod] = useState<ShippingMethod>("express");
  const [buyColor, setBuyColor] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [sellPlatformFee, setSellPlatformFee] = useState("0.00");
  const [sellPlatformFeeEdited, setSellPlatformFeeEdited] = useState(false);
  const [sellConsumableCost, setSellConsumableCost] = useState("3.15");
  const [period, setPeriod] = useState<TradeLedgerPeriod>("week");
  const [salesView, setSalesView] = useState<"pending" | "sold">("pending");
  const [selectedLotId, setSelectedLotId] = useState("");
  const [saleMode, setSaleMode] = useState<"listed" | "sold">("sold");
  const [entrySheet, setEntrySheet] = useState<"buy" | "sell" | null>(null);
  const [editingProcurement, setEditingProcurement] = useState<AdminTradeProcurement | null>(null);
  const [updatingSaleId, setUpdatingSaleId] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  function closeEntrySheet() {
    setEntrySheet(null);
    setEditingProcurement(null);
  }

  function openNewProcurement() {
    setEditingProcurement(null);
    setBuyPlatform("xianyu");
    setBuySubcategory("平衡车");
    setBuyShippingMethod("express");
    setBuyColor("");
    setEntrySheet("buy");
  }

  function openSaleEntry(procurementOrderId: number, mode: "listed" | "sold" = "sold") {
    setSellPlatform("xianyu");
    setSellShippingMethod("express");
    setSellPrice("");
    setSellPlatformFee("0.00");
    setSellPlatformFeeEdited(false);
    setSellConsumableCost("3.15");
    setSelectedLotId(procurementOrderId ? String(procurementOrderId) : "");
    setSaleMode(mode);
    setEntrySheet("sell");
  }

  function handleSellPlatformChange(platform: TradePlatform) {
    setSellPlatform(platform);
    setSellPlatformFeeEdited(false);
    setSellPlatformFee(platform === "xianyu" ? calculateXianyuPlatformFee(sellPrice) : "0.00");
    setSellConsumableCost(platform === "xianyu" && sellShippingMethod === "express" ? "3.15" : "0.00");
  }

  function openProcurementEditor(item: AdminTradeProcurement) {
    if (Number(item.sold_quantity) > 0) {
      return;
    }
    setEditingProcurement(item);
    setBuyPlatform(item.platform as TradePlatform);
    setBuySubcategory(item.subcategory || "平衡车");
    setBuyShippingMethod(item.shipping_method);
    setBuyColor(item.color || "");
    setEntrySheet("buy");
  }

  async function refreshLedger() {
    const data = await loadAdminTradeLedger(token, 200);
    setLedger(data);
    setStatus((current) => (current === "done" ? "done" : "ready"));
  }

  useEffect(() => {
    setStatus("loading");
    void refreshLedger().catch(() => setStatus("error"));
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setRadarStatus("loading");
    void loadOperatorRadar(token)
      .then((payload) => {
        if (!cancelled) {
          setRadar(payload.opportunities);
          setRadarStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRadarStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const openLots = useMemo(() => {
    return (ledger?.procurements ?? []).filter((item) => Number(item.sold_quantity) < Number(item.quantity));
  }, [ledger]);

  const selectedLot = openLots.find((item) => String(item.id) === selectedLotId);

  useEffect(() => {
    if (!entrySheet) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setEntrySheet(null);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [entrySheet]);

  const recentProcurements = useMemo(
    // “最近”按录入时间计算，避免用户补录旧采购时间后，刚保存的记录立即从列表消失。
    () => (ledger?.procurements ?? []).filter((item) => isWithinLastDays(item.created_at, 30)),
    [ledger]
  );
  const recentSales = useMemo(
    () => (ledger?.sales ?? []).filter((sale) => isWithinLastDays(sale.created_at, 30)),
    [ledger]
  );
  const visibleProcurements = recentProcurements.length > 0 ? recentProcurements : (ledger?.procurements ?? []);
  const visibleSales = recentSales.length > 0 ? recentSales : (ledger?.sales ?? []);

  async function submitBuy(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("loading");
    setErrorMessage("");
    try {
      const input = {
        platform: buyPlatform,
        category: parentCategoryForProduct(buySubcategory),
        subcategory: buySubcategory,
        color: buyColor,
        giftInfo: String(formData.get("giftInfo") ?? "").trim(),
        title: String(formData.get("title") ?? ""),
        quantity: Number(formData.get("quantity") ?? 1),
        buyPrice: Number(formData.get("buyPrice")),
        shippingMethod: buyShippingMethod,
        freight: buyShippingMethod === "express" ? Number(formData.get("freight") || 0) : 0,
        consumableCost: buyShippingMethod === "express" ? Number(formData.get("consumableCost") || 0) : 0,
        miscCost: editingProcurement ? Number(editingProcurement.misc_cost) || 0 : Number(formData.get("miscCost") || 0),
        externalRef: String(formData.get("externalRef") ?? "").trim() || null,
        purchasedAt: String(formData.get("purchasedAt") ?? "").trim() || null,
        city: editingProcurement?.city ?? "全国"
      };
      if (editingProcurement) {
        await updateAdminTradeProcurement(token, editingProcurement.id, input);
      } else {
        await createAdminTradeProcurement(token, input);
      }
      form.reset();
      setBuyColor("");
      closeEntrySheet();
      setStatus("done");
    } catch {
      setErrorMessage("采购单未保存，请检查商品、数量、金额和管理员权限。");
      setStatus("error");
      return;
    }

    try {
      await refreshLedger();
      onChanged();
    } catch {
      setErrorMessage("采购单已保存，但刷新列表失败，请下拉页面重试。");
      setStatus("error");
    }
  }

  async function submitSell(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("loading");
    setErrorMessage("");
    if (!selectedLotId || !selectedLot) {
      setErrorMessage("关联采购单已失效，请返回销售页重新选择商品。");
      setStatus("error");
      return;
    }
    try {
      await createAdminTradeSale(token, {
        procurementOrderId: Number(selectedLotId),
        platform: sellPlatform,
        giftInfo: String(formData.get("giftInfo") ?? "").trim(),
        quantity: Number(formData.get("quantity") ?? 1),
        salePrice: Number(sellPrice),
        shippingMethod: sellShippingMethod,
        freight: sellShippingMethod === "express" ? Number(formData.get("freight") || 0) : 0,
        consumableCost: Number(sellConsumableCost || 0),
        platformFee: Number(sellPlatformFee || 0),
        miscCost: Number(formData.get("miscCost") || 0),
        externalRef: String(formData.get("externalRef") ?? "").trim() || null,
        listedAt: String(formData.get("listedAt") ?? "").trim() || null,
        soldAt: saleMode === "sold" ? String(formData.get("soldAt") ?? "").trim() || null : null
      });
      form.reset();
      setSelectedLotId("");
      setSellPrice("");
      setSellPlatformFee("0.00");
      setSellPlatformFeeEdited(false);
      setSellConsumableCost("3.15");
      setEntrySheet(null);
      setStatus("done");
    } catch {
      setErrorMessage("销售单未保存，请检查关联采购、剩余数量、金额和管理员权限。");
      setStatus("error");
      return;
    }

    try {
      await refreshLedger();
      onChanged();
    } catch {
      setErrorMessage("销售单已保存，但刷新列表失败，请下拉页面重试。");
      setStatus("error");
    }
  }

  async function refreshWorkspace() {
    if (refreshing) {
      return;
    }
    setRefreshing(true);
    setStatus("loading");
    const refreshRadar = section === "radar";
    if (refreshRadar) {
      setRadarStatus("loading");
    }
    setErrorMessage("");

    const [ledgerResult, radarResult] = await Promise.allSettled([
      loadAdminTradeLedger(token, 200),
      refreshRadar ? loadOperatorRadar(token) : Promise.resolve(null)
    ]);
    const ledgerFailed = ledgerResult.status === "rejected";

    if (!ledgerFailed) {
      setLedger(ledgerResult.value);
    }
    if (refreshRadar && radarResult.status === "fulfilled" && radarResult.value) {
      setRadar(radarResult.value.opportunities);
      setRadarStatus("ready");
    } else if (refreshRadar) {
      setRadarStatus("error");
    }

    const radarFailed = refreshRadar && radarResult.status === "rejected";
    if (ledgerFailed || radarFailed) {
      setStatus("error");
      setErrorMessage(ledgerFailed ? "经营数据更新失败，请下拉重试。" : "机会数据更新失败，请稍后下拉重试。");
    } else {
      setStatus("ready");
    }
    setRefreshing(false);
  }

  async function changeSaleStatus(sale: AdminTradeSale, nextStatus: "sold" | "cancelled") {
    setUpdatingSaleId(sale.id);
    setErrorMessage("");
    try {
      await updateAdminTradeSaleStatus(token, sale.id, {
        status: nextStatus,
        soldAt: nextStatus === "sold" ? toDateTimeLocalValue() : null,
        operatorNote: nextStatus === "sold" ? "运营确认成交" : "运营取消上架"
      });
      await refreshLedger();
      onChanged();
    } catch {
      setErrorMessage(nextStatus === "sold" ? "确认成交失败，请下拉页面重试。" : "取消上架失败，请下拉页面重试。");
      setStatus("error");
    } finally {
      setUpdatingSaleId(null);
    }
  }

  return (
    <PullToRefresh onRefresh={refreshWorkspace} disabled={entrySheet !== null} className="ledger-refresh-surface">
    <section className="ledger-workspace">
      <nav className="admin-module-nav" aria-label="经营模块" role="tablist">
        {workspaceTabs.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={section === item.id ? "active" : ""}
              aria-selected={section === item.id}
              onClick={() => {
                setSection(item.id);
                setEntrySheet(null);
              }}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <LoadingOverlay active={status === "loading" && !ledger} label="正在读取进销存数据" />

      {status === "error" && !ledger && <p className="admin-panel-error" role="alert">经营数据读取失败，请退出后重新登录。</p>}

      {ledger && (
        <div
          className={`admin-module-panel${section === "buy" || section === "sell" ? " admin-trade-panel" : ""}${section === "sell" ? ` admin-sales-panel admin-sales-view-${salesView}` : ""}${section === "ledger" || section === "analytics" ? " ledger-data-panel" : ""}`}
          key={section}
        >
          {section === "radar" && (
            <OpportunityRadar
              opportunities={radar}
              status={radarStatus}
              onOpenProcurement={() => setSection("buy")}
            />
          )}
          {section === "buy" && (
            <>
              <div className="admin-entry-toolbar compact">
                <div className="admin-entry-actions">
                  <button className="secondary-button admin-add-button" type="button" onClick={openNewProcurement}>
                    <Plus size={15} />添加采购
                  </button>
                </div>
              </div>
              <RecentProcurements
                items={visibleProcurements}
                onEdit={openProcurementEditor}
                onSell={(item) => {
                  setSection("sell");
                  openSaleEntry(item.id, "sold");
                }}
              />
              {entrySheet === "buy" && <EntrySheet title={editingProcurement ? "编辑采购" : "添加采购"} onClose={closeEntrySheet}>
              <form className="ledger-form ledger-form-buy" onSubmit={submitBuy} key={`trade-buy-form-${editingProcurement?.id ?? "new"}`}>
                <PlatformPicker value={buyPlatform} onChange={setBuyPlatform} />
                <label>
                  <span>品类</span>
                  <select value={buySubcategory} onChange={(event) => setBuySubcategory(event.target.value)}>
                    {procurementCategoryGroups.map((group) => (
                      <optgroup label={group.label} key={group.label}>
                        {group.options.map((option) => <option value={option} key={option}>{option}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label className="ledger-wide">
                  <span>商品</span>
                  <input name="title" placeholder="名称、型号或版本" defaultValue={editingProcurement?.title ?? ""} required autoComplete="off" />
                </label>
                <label>
                  <span>数量</span>
                  <input name="quantity" type="number" inputMode="numeric" min="1" step="1" defaultValue={editingProcurement?.quantity ?? 1} required />
                </label>
                <label>
                  <span>采购单价</span>
                  <input name="buyPrice" type="number" inputMode="decimal" min="1" step="0.01" placeholder="¥ 0.00" defaultValue={editingProcurement?.buy_price ?? ""} required />
                </label>
                <label>
                  <span>采购时间</span>
                  <input name="purchasedAt" type="date" defaultValue={toDateInputValue(editingProcurement?.purchased_at)} required />
                </label>
                <label>
                  <span>颜色</span>
                  <select value={buyColor} onChange={(event) => setBuyColor(event.target.value)}>
                    <option value="">请选择</option>
                    {quickColors.map((color) => <option value={color} key={color}>{color}</option>)}
                  </select>
                </label>
                <label>
                  <span>发货方式</span>
                  <select value={buyShippingMethod} onChange={(event) => setBuyShippingMethod(event.target.value as ShippingMethod)}>
                    <option value="express">快递</option>
                    <option value="pickup">自提</option>
                  </select>
                </label>
                {buyShippingMethod === "express" && (
                  <>
                    <label>
                      <span>采购运费</span>
                      <input name="freight" type="number" inputMode="decimal" min="0" step="0.01" defaultValue={editingProcurement?.inbound_freight ?? "0.00"} required />
                    </label>
                    <label>
                      <span>包装耗材</span>
                      <input name="consumableCost" type="number" inputMode="decimal" min="0" step="0.01" defaultValue={editingProcurement?.consumable_cost ?? "0.00"} required />
                    </label>
                  </>
                )}
                <label>
                  <span>赠品</span>
                  <input name="giftInfo" maxLength={240} placeholder="如：充电器 × 1，可留空" defaultValue={editingProcurement?.gift_info ?? ""} autoComplete="off" />
                </label>
                <button className="primary-button ledger-submit" type="submit" disabled={status === "loading"}>
                  <Save size={14} />{status === "loading" ? "保存中" : editingProcurement ? "保存修改" : "保存采购"}
                </button>
              </form>
              </EntrySheet>}
            </>
          )}

          {section === "sell" && (
            <>
              <div className="sales-view-tabs" role="tablist" aria-label="销售状态">
                <button
                  type="button"
                  role="tab"
                  aria-selected={salesView === "pending"}
                  className={salesView === "pending" ? "active" : ""}
                  onClick={() => setSalesView("pending")}
                >
                  待销售<span>{openLots.length + visibleSales.filter((sale) => sale.status === "listed").length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={salesView === "sold"}
                  className={salesView === "sold" ? "active" : ""}
                  onClick={() => setSalesView("sold")}
                >
                  已销售<span>{visibleSales.filter((sale) => sale.status === "sold").length}</span>
                </button>
              </div>
              {salesView === "pending" && (
                <>
                  <SellableProcurements
                    items={openLots}
                    onSell={(item) => openSaleEntry(item.id, "sold")}
                    onList={(item) => openSaleEntry(item.id, "listed")}
                  />
                  <RecentSales
                    title="已上架"
                    emptyMessage="暂无已上架商品。"
                    sales={visibleSales.filter((sale) => sale.status === "listed")}
                    updatingSaleId={updatingSaleId}
                    onSaleStatusChange={changeSaleStatus}
                  />
                </>
              )}
              {salesView === "sold" && (
                <RecentSales
                  title="成交记录"
                  emptyMessage="暂无成交记录。"
                  sales={visibleSales.filter((sale) => sale.status === "sold")}
                  updatingSaleId={updatingSaleId}
                  onSaleStatusChange={changeSaleStatus}
                />
              )}
              {entrySheet === "sell" && <EntrySheet
                title={saleMode === "sold" ? "卖出商品" : "上架商品"}
                onClose={() => setEntrySheet(null)}
                footer={(
                  <button
                    className="primary-button ledger-submit"
                    type="submit"
                    form="trade-sale-form"
                    disabled={status === "loading" || openLots.length === 0}
                  >
                    <Save size={16} />{status === "loading" ? "保存中" : saleMode === "sold" ? "确认卖出" : "确认上架"}
                  </button>
                )}
              >
              <form id="trade-sale-form" className="ledger-form" onSubmit={submitSell} key="trade-sell-form">
                {selectedLot && <LotPreview lot={selectedLot} />}
                <PlatformPicker value={sellPlatform} onChange={handleSellPlatformChange} />
                <label>
                  <span>发货方式</span>
                  <select
                    value={sellShippingMethod}
                    onChange={(event) => {
                      const method = event.target.value as ShippingMethod;
                      setSellShippingMethod(method);
                      setSellConsumableCost(sellPlatform === "xianyu" && method === "express" ? "3.15" : "0.00");
                    }}
                  >
                    <option value="express">快递</option>
                    <option value="pickup">自提</option>
                  </select>
                </label>
                {sellShippingMethod === "express" && (
                  <label>
                    <span>销售运费</span>
                    <input name="freight" type="number" inputMode="decimal" min="0" step="0.01" defaultValue="0.00" required />
                  </label>
                )}
                <label>
                  <span>数量</span>
                  <input name="quantity" type="number" inputMode="numeric" min="1" step="1" defaultValue="1" max={selectedLot ? Number(selectedLot.quantity) - Number(selectedLot.sold_quantity) : undefined} required />
                </label>
                <label>
                  <span>销售单价</span>
                  <input
                    name="salePrice"
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="0.01"
                    placeholder="¥ 0.00"
                    value={sellPrice}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSellPrice(value);
                      if (sellPlatform === "xianyu" && !sellPlatformFeeEdited) {
                        setSellPlatformFee(calculateXianyuPlatformFee(value));
                      }
                    }}
                    required
                  />
                </label>
                <div className={`ledger-time-row ledger-wide ${saleMode === "listed" ? "single" : ""}`}>
                  <label>
                    <span>上架时间</span>
                    <input name="listedAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
                  </label>
                  {saleMode === "sold" && (
                    <label>
                      <span>下架时间</span>
                      <input name="soldAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
                    </label>
                  )}
                </div>
                <label className="ledger-wide">
                  <span>赠品</span>
                  <input name="giftInfo" maxLength={240} placeholder="如：保护壳 × 1，可留空" autoComplete="off" />
                </label>
                <label>
                  <span>平台服务费{sellPlatform === "xianyu" ? "（0.6%）" : ""}</span>
                  <input
                    name="platformFee"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={sellPlatformFee}
                    onChange={(event) => {
                      setSellPlatformFeeEdited(true);
                      setSellPlatformFee(event.target.value);
                    }}
                    required
                  />
                </label>
                <label>
                  <span>包装耗材</span>
                  <input
                    name="consumableCost"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={sellConsumableCost}
                    onChange={(event) => setSellConsumableCost(event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>其他费用</span>
                  <input name="miscCost" type="number" inputMode="decimal" min="0" step="0.01" defaultValue="0.00" required />
                </label>
                <label>
                  <span>平台单号</span>
                  <input name="externalRef" placeholder="可选，不填也能保存" autoComplete="off" />
                </label>
              </form>
              </EntrySheet>}
            </>
          )}

          {section === "ledger" && (
            <>
              <LedgerMetrics ledger={ledger} />
              <LedgerFinanceOverview ledger={ledger} />
              <LedgerHistory
                ledger={ledger}
                updatingSaleId={updatingSaleId}
                onSaleStatusChange={changeSaleStatus}
                onEditProcurement={openProcurementEditor}
              />
            </>
          )}

          {section === "analytics" && (
            <>
              <LedgerAnalytics ledger={ledger} period={period} onPeriodChange={setPeriod} />
            </>
          )}
        </div>
      )}

      {status === "error" && ledger && <p className="form-message error" role="alert">{errorMessage || "操作失败，请下拉页面重试。"}</p>}
    </section>
    </PullToRefresh>
  );
}

function OpportunityRadar({
  opportunities,
  status,
  onOpenProcurement
}: {
  opportunities: Opportunity[];
  status: "loading" | "ready" | "error";
  onOpenProcurement: () => void;
}) {
  const [filter, setFilter] = useState<"action" | "all">("action");
  const actionable = opportunities.filter((item) => item.action === "bid" || item.action === "inspect");
  const visible = filter === "action" ? actionable : opportunities;

  return (
    <section className="opportunity-radar" aria-label="机会采买探测">
      <div className="opportunity-radar-toolbar">
        <div className="opportunity-filter" role="tablist" aria-label="机会筛选">
          <button type="button" role="tab" aria-selected={filter === "action"} className={filter === "action" ? "active" : ""} onClick={() => setFilter("action")}>
            待处理 <b>{actionable.length}</b>
          </button>
          <button type="button" role="tab" aria-selected={filter === "all"} className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
            全部 <b>{opportunities.length}</b>
          </button>
        </div>
      </div>

      <LoadingOverlay active={status === "loading"} label="正在读取机会源" />
      {status === "error" && <p className="admin-panel-error" role="alert">机会源暂时不可用，请下拉页面重试。</p>}
      {status === "ready" && visible.length === 0 && (
        <div className="opportunity-radar-empty">
          <ShieldCheck size={20} />
          <strong>{filter === "action" ? "暂无待处理机会" : "暂无真实机会"}</strong>
          <span>有经过价格、卖家和风险复核的机会后，会优先出现在这里。</span>
        </div>
      )}
      {status === "ready" && visible.length > 0 && (
        <div className="opportunity-radar-list">
          {visible.slice(0, 12).map((item) => {
            const margin = item.fairPrice - item.askPrice;
            const actionLabel = item.action === "bid" ? "转采购" : item.action === "inspect" ? "安排验货" : "继续观察";
            return (
              <article className="opportunity-radar-card" key={item.id}>
                <div className="opportunity-radar-card-main">
                  <div className="opportunity-radar-meta">
                    <span>{item.source === "xianyu" ? "闲鱼" : item.source === "partner" ? "合作方" : "人工"}</span>
                    <span className={`risk ${item.risk}`}>{item.risk === "low" ? "低风险" : item.risk === "medium" ? "需复核" : "高风险"}</span>
                    <span>{item.freshnessMinutes} 分钟前</span>
                  </div>
                  <strong>{item.title}</strong>
                  <small>{item.city} · 采买 {currency.format(item.askPrice)} · 参考 {currency.format(item.fairPrice)} · 预计毛利 {currency.format(margin)}</small>
                  <span className="opportunity-radar-signal"><ClipboardCheck size={14} />{item.action === "bid" ? "价格和风险已达采买线" : item.action === "inspect" ? "先确认成色与结构" : "等待新的价格或卖家信号"}</span>
                </div>
                <div className="opportunity-radar-card-actions">
                  <strong className="opportunity-score">{item.score}<small>分</small></strong>
                  {item.sourceUrl ? <a href={item.sourceUrl} target="_blank" rel="noreferrer" aria-label={`打开 ${item.title} 原商品`}><ExternalLink size={13} />原商品</a> : <span>缺源链接</span>}
                  <button type="button" onClick={onOpenProcurement}>{actionLabel}</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function LedgerMetrics({ ledger }: { ledger: AdminTradeLedger }) {
  return (
    <div className="ledger-metrics">
      <article><span>采购投入</span><strong>{currency.format(ledger.summary.buyCost)}</strong><small>{ledger.summary.procurementCount} 笔采购</small></article>
      <article><span>已售净收入</span><strong>{currency.format(ledger.summary.netRevenue)}</strong><small>{ledger.summary.saleCount} 笔成交</small></article>
      <article><span>全部成本</span><strong>{currency.format(ledger.summary.expense)}</strong><small>含整备及销售费用</small></article>
      <article className={ledger.summary.realizedMargin >= 0 ? "positive" : "negative"}><span>已售毛利</span><strong>{currency.format(ledger.summary.realizedMargin)}</strong><small>毛利率 {formatPercent(ledger.summary.marginRate)}</small></article>
    </div>
  );
}

function LedgerFinanceOverview({ ledger }: { ledger: AdminTradeLedger }) {
  const { summary } = ledger;
  const inflow = Math.max(0, Number(summary.netRevenue));
  const outflow = Math.max(0, Number(summary.expense));
  const scale = Math.max(inflow, outflow, 1);
  const marginRate = summary.marginRate === null ? "—" : `${summary.marginRate.toFixed(1)}%`;
  const barWidth = (value: number) => `${Math.max(value > 0 ? 6 : 0, Math.min(100, value / scale * 100))}%`;

  return (
    <section className="finance-overview" aria-label="财务概览">
      <header className="finance-overview-head">
        <div>
          <p className="eyebrow">财务概览</p>
          <h4>收入与支出</h4>
        </div>
        <span className={summary.realizedMargin >= 0 ? "positive" : "negative"}>毛利率 {marginRate}</span>
      </header>
      <div className="finance-bars">
        <div className="finance-bar-row">
          <span>净收入</span>
          <div className="finance-bar-track" role="progressbar" aria-label="净收入占比" aria-valuenow={inflow} aria-valuemin={0} aria-valuemax={scale}>
            <i className="income" style={{ width: barWidth(inflow) }} />
          </div>
          <strong>{currency.format(inflow)}</strong>
        </div>
        <div className="finance-bar-row">
          <span>总支出</span>
          <div className="finance-bar-track" role="progressbar" aria-label="总支出占比" aria-valuenow={outflow} aria-valuemin={0} aria-valuemax={scale}>
            <i className="outflow" style={{ width: barWidth(outflow) }} />
          </div>
          <strong>{currency.format(outflow)}</strong>
        </div>
      </div>
      <div className="finance-breakdown">
        <span><small>采购</small><b>{currency.format(summary.buyCost)}</b></span>
        <span><small>整备</small><b>{currency.format(summary.refurbishmentCost)}</b></span>
        <span><small>销售费用</small><b>{currency.format(summary.sellingExpense)}</b></span>
        <span><small>现金差额</small><b className={summary.cashMargin >= 0 ? "positive" : "negative"}>{currency.format(summary.cashMargin)}</b></span>
      </div>
    </section>
  );
}

function RecentProcurements({
  items,
  onEdit,
  onSell
}: {
  items: AdminTradeProcurement[];
  onEdit: (item: AdminTradeProcurement) => void;
  onSell: (item: AdminTradeProcurement) => void;
}) {
  const { visibleItems, hasMore, loadMore } = useProgressiveItems(items);

  return (
    <section className="admin-recent" aria-label="最近采购">
      <header><h4>采购记录</h4><span>{items.length} 笔</span></header>
      {items.length === 0 ? <p className="ledger-empty">还没有采购记录。</p> : (
        <div
          className="admin-list-viewport"
          onScroll={(event) => {
            const target = event.currentTarget;
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 72) {
              loadMore();
            }
          }}
        >
          <div className="admin-compact-list">
            {visibleItems.map((item) => {
            const remaining = Number(item.quantity) - Number(item.sold_quantity);
            return (
              <article className={`${tradeCategoryClass(item.category, item.subcategory)}${remaining <= 0 ? " is-sold" : ""}`} key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <small>
                    {translateTradePlatform(String(item.platform))} · {shippingMethodLabel(item.shipping_method)} · #{item.id}
                    {Number(item.inbound_freight) > 0 && ` · 快递费 ${currency.format(Number(item.inbound_freight))}`}
                  </small>
                </div>
                <div>
                  <strong>{currency.format(procurementTotalCost(item))}</strong>
                  <small>库存 {remaining}/{item.quantity}</small>
                </div>
                <div className="admin-compact-actions">
                  <small className="admin-compact-date">{formatProcurementDate(item.purchased_at)}</small>
                  <div className="admin-compact-buttons">
                    {remaining > 0 && (
                      <button className="admin-compact-sell" type="button" onClick={() => onSell(item)}>
                        卖出
                      </button>
                    )}
                    {Number(item.sold_quantity) === 0 && (
                      <button className="admin-compact-edit" type="button" onClick={() => onEdit(item)}>
                        编辑
                      </button>
                    )}
                  </div>
                </div>
                {remaining <= 0 && <span className="admin-sold-stamp">已卖出</span>}
              </article>
            );
            })}
          </div>
          {hasMore && <p className="admin-list-load-status" role="status">继续下滑加载更多</p>}
        </div>
      )}
    </section>
  );
}

function SellableProcurements({
  items,
  onSell,
  onList
}: {
  items: AdminTradeProcurement[];
  onSell: (item: AdminTradeProcurement) => void;
  onList: (item: AdminTradeProcurement) => void;
}) {
  const { visibleItems, hasMore, loadMore } = useProgressiveItems(items);

  return (
    <section className="admin-recent admin-sale-queue" aria-label="待售商品">
      <header><h4>待售商品</h4><span>{items.length} 件</span></header>
      {items.length === 0 ? <p className="ledger-empty">没有可卖出的采购商品。</p> : (
        <div
          className="admin-list-viewport"
          onScroll={(event) => {
            const target = event.currentTarget;
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 72) {
              loadMore();
            }
          }}
        >
          <div className="admin-compact-list">
            {visibleItems.map((item) => {
              const remaining = Number(item.quantity) - Number(item.sold_quantity);
              return (
                <article className={tradeCategoryClass(item.category, item.subcategory)} key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <small>采购 #{item.id} · 余 {remaining}/{item.quantity} 件 · 成本 {currency.format(procurementUnitCost(item))}</small>
                  </div>
                  <div className="admin-sale-queue-actions">
                    <button className="admin-compact-list-button" type="button" onClick={() => onList(item)}><Tag size={12} />上架</button>
                    <button className="admin-compact-sell" type="button" onClick={() => onSell(item)}>卖出</button>
                  </div>
                </article>
              );
            })}
          </div>
          {hasMore && <p className="admin-list-load-status" role="status">继续下滑加载更多</p>}
        </div>
      )}
    </section>
  );
}

function EntrySheet({ title, onClose, children, footer }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLButtonElement>(".sheet-close")?.focus();
    return () => { if (trigger?.isConnected) trigger.focus(); };
  }, []);

  function keepFocusInDialog(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
    )).filter((element) => element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  return createPortal(
    <div
      className="sheet-backdrop entry-sheet-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section ref={dialogRef} onKeyDown={keepFocusInDialog} className={`entry-sheet${footer ? " entry-sheet-with-footer" : ""}`} role="dialog" aria-modal="true" aria-labelledby="entry-sheet-title">
        <div className="sheet-handle" aria-hidden="true" />
        <header className="entry-sheet-head">
          <h3 id="entry-sheet-title">{title}</h3>
          <button className="sheet-close" type="button" aria-label={`关闭${title}`} onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="entry-sheet-body">{children}</div>
        {footer && <footer className="entry-sheet-footer">{footer}</footer>}
      </section>
    </div>,
    document.body
  );
}

function RecentSales({ title, emptyMessage, sales, updatingSaleId, onSaleStatusChange }: {
  title: string;
  emptyMessage: string;
  sales: AdminTradeSale[];
  updatingSaleId: number | null;
  onSaleStatusChange: (sale: AdminTradeSale, status: "sold" | "cancelled") => Promise<void>;
}) {
  const [pendingCancelId, setPendingCancelId] = useState<number | null>(null);
  const { visibleItems, hasMore, loadMore } = useProgressiveItems(sales);

  return (
    <section className="admin-recent" aria-label="最近销售">
      <header><h4>{title}</h4><span>{sales.length} 笔</span></header>
      {sales.length === 0 ? <p className="ledger-empty">{emptyMessage}</p> : (
        <div
          className="admin-list-viewport"
          onScroll={(event) => {
            const target = event.currentTarget;
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 72) {
              loadMore();
            }
          }}
        >
          <div className="admin-compact-list">
            {visibleItems.map((sale) => (
              <article className={tradeCategoryClass(sale.category, sale.procurement_subcategory)} key={sale.id}>
                <div>
                  <strong>{sale.title}</strong>
                  <small>{translateTradePlatform(String(sale.platform))} · {shippingMethodLabel(sale.shipping_method)} · #{sale.id} · {formatResaleStatus(sale.status)}</small>
                  <small className="admin-sale-source">
                    采购 #{sale.procurement_order_id} · {sale.procurement_title} · {formatProcurementDate(sale.purchased_at)}
                  </small>
                </div>
                <div>
                  <strong>{currency.format(Number(sale.sale_price))}</strong>
                  <small>毛利 {currency.format(Number(sale.margin))}</small>
                </div>
                {sale.status === "listed" && pendingCancelId !== sale.id && (
                  <div className="admin-compact-actions">
                    <button type="button" disabled={updatingSaleId === sale.id} onClick={() => onSaleStatusChange(sale, "sold")}>成交</button>
                    <button type="button" disabled={updatingSaleId === sale.id} onClick={() => setPendingCancelId(sale.id)}>取消</button>
                  </div>
                )}
                {sale.status === "listed" && pendingCancelId === sale.id && (
                  <div className="admin-compact-actions confirm">
                    <button type="button" onClick={() => {
                      setPendingCancelId(null);
                      void onSaleStatusChange(sale, "cancelled");
                    }}>确认</button>
                    <button type="button" onClick={() => setPendingCancelId(null)}>保留</button>
                  </div>
                )}
              </article>
            ))}
          </div>
          {hasMore && <p className="admin-list-load-status" role="status">继续下滑加载更多</p>}
        </div>
      )}
    </section>
  );
}

type AnalyticsSkuRow = {
  key: string;
  title: string;
  subcategory: string;
  category: Category;
  procurementCount: number;
  purchasedUnits: number;
  soldUnits: number;
  listedUnits: number;
  inventoryUnits: number;
  inventoryValue: number;
  netRevenue: number;
  realizedMargin: number;
  oldestInventoryDays: number | null;
  averageCycleDays: number | null;
  sampleCount: number;
};

type AnalyticsAction = {
  tone: "warn" | "danger" | "positive" | "neutral";
  title: string;
  detail: string;
};

function analyticsSkuKey(title: string, subcategory: string) {
  return `${title.trim().toLocaleLowerCase("zh-CN")}::${subcategory.trim().toLocaleLowerCase("zh-CN")}`;
}

function daysSince(value: string | null | undefined) {
  const timestamp = timeValue(value);
  return timestamp > 0 ? Math.max(0, Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000))) : null;
}

function saleCycleDays(sale: AdminTradeSale) {
  const listedAt = timeValue(sale.listed_at ?? sale.created_at);
  const soldAt = timeValue(sale.sold_at);
  return listedAt > 0 && soldAt > listedAt ? (soldAt - listedAt) / (24 * 60 * 60 * 1000) : null;
}

function buildAnalyticsSkuRows(ledger: AdminTradeLedger) {
  const rows = new Map<string, AnalyticsSkuRow>();
  const procurementKeys = new Map<number, string>();
  const cycleValues = new Map<string, number[]>();

  for (const item of ledger.procurements) {
    if (item.status === "cancelled") {
      continue;
    }
    const subcategory = item.subcategory || categoryLabel(item.category);
    const key = analyticsSkuKey(item.title, subcategory);
    procurementKeys.set(item.id, key);
    const quantity = Math.max(0, Number(item.quantity));
    const remaining = Math.max(0, quantity - Number(item.sold_quantity));
    const unitCost = (procurementTotalCost(item) + Number(item.refurbishment_cost)) / Math.max(quantity, 1);
    const currentAge = remaining > 0 ? daysSince(item.purchased_at ?? item.created_at) : null;
    const existing = rows.get(key) ?? {
      key,
      title: item.title,
      subcategory,
      category: item.category,
      procurementCount: 0,
      purchasedUnits: 0,
      soldUnits: 0,
      listedUnits: 0,
      inventoryUnits: 0,
      inventoryValue: 0,
      netRevenue: 0,
      realizedMargin: 0,
      oldestInventoryDays: null,
      averageCycleDays: null,
      sampleCount: 0
    };
    existing.procurementCount += 1;
    existing.purchasedUnits += quantity;
    existing.inventoryUnits += remaining;
    existing.inventoryValue += unitCost * remaining;
    existing.sampleCount += 1;
    if (currentAge !== null) {
      existing.oldestInventoryDays = Math.max(existing.oldestInventoryDays ?? 0, currentAge);
    }
    rows.set(key, existing);
  }

  for (const sale of ledger.sales) {
    if (sale.status === "cancelled") {
      continue;
    }
    const key = procurementKeys.get(sale.procurement_order_id) ?? analyticsSkuKey(sale.title, sale.procurement_subcategory || categoryLabel(sale.category));
    const existing = rows.get(key) ?? {
      key,
      title: sale.title,
      subcategory: sale.procurement_subcategory || categoryLabel(sale.category),
      category: sale.category,
      procurementCount: 0,
      purchasedUnits: 0,
      soldUnits: 0,
      listedUnits: 0,
      inventoryUnits: 0,
      inventoryValue: 0,
      netRevenue: 0,
      realizedMargin: 0,
      oldestInventoryDays: null,
      averageCycleDays: null,
      sampleCount: 0
    };
    if (sale.status === "listed") {
      existing.listedUnits += Number(sale.quantity);
    } else {
      existing.soldUnits += Number(sale.quantity);
      existing.netRevenue += saleNetAmount(sale);
      existing.realizedMargin += Number(sale.margin);
      const cycle = saleCycleDays(sale);
      if (cycle !== null) {
        const values = cycleValues.get(key) ?? [];
        values.push(cycle);
        cycleValues.set(key, values);
      }
    }
    existing.sampleCount += 1;
    rows.set(key, existing);
  }

  for (const [key, row] of rows) {
    const cycles = cycleValues.get(key) ?? [];
    row.averageCycleDays = cycles.length > 0 ? cycles.reduce((sum, value) => sum + value, 0) / cycles.length : null;
  }
  return Array.from(rows.values()).sort((a, b) => b.inventoryValue - a.inventoryValue || a.title.localeCompare(b.title, "zh-CN"));
}

function analyticsSkuStatus(row: AnalyticsSkuRow) {
  const marginRate = row.netRevenue > 0 ? row.realizedMargin / row.netRevenue : null;
  if ((row.oldestInventoryDays ?? 0) >= 30) {
    return { label: "需清理", tone: "danger" as const };
  }
  if (row.sampleCount < 3) {
    return { label: "样本不足", tone: "neutral" as const };
  }
  if (marginRate !== null && marginRate < 0.1) {
    return { label: "控制采购", tone: "warn" as const };
  }
  if (row.averageCycleDays !== null && row.averageCycleDays <= 14 && marginRate !== null && marginRate >= 0.15) {
    return { label: "可放大", tone: "positive" as const };
  }
  return { label: "维持", tone: "neutral" as const };
}

function buildAnalyticsActions(rows: AnalyticsSkuRow[]): AnalyticsAction[] {
  if (rows.length === 0) {
    return [{ tone: "neutral", title: "等待真实样本", detail: "录入采购与销售后，这里会生成可执行的经营建议。" }];
  }
  const actions: AnalyticsAction[] = [];
  const aged = rows.filter((row) => row.inventoryUnits > 0 && (row.oldestInventoryDays ?? 0) >= 15).sort((a, b) => (b.oldestInventoryDays ?? 0) - (a.oldestInventoryDays ?? 0));
  if (aged.length > 0) {
    actions.push({
      tone: aged[0].oldestInventoryDays! >= 30 ? "danger" : "warn",
      title: `${aged.length} 个 SKU 需要处理库存`,
      detail: `${aged[0].title} 已占用 ${aged[0].oldestInventoryDays} 天，建议先调价或停止补货。`
    });
  }
  const lowMargin = rows.filter((row) => row.soldUnits > 0 && row.netRevenue > 0 && row.realizedMargin / row.netRevenue < 0.1);
  if (lowMargin.length > 0) {
    actions.push({ tone: "warn", title: `${lowMargin.length} 个 SKU 毛利偏低`, detail: `${lowMargin[0].title} 的实际毛利率低于 10%，优先检查运费和整备成本。` });
  }
  const fast = rows.find((row) => row.averageCycleDays !== null && row.averageCycleDays <= 14 && row.sampleCount >= 3);
  if (fast) {
    actions.push({ tone: "positive", title: `${fast.title} 周转较快`, detail: `已有 ${fast.soldUnits} 件成交，平均 ${fast.averageCycleDays!.toFixed(1)} 天，可小量验证是否放大。` });
  }
  if (actions.length === 0) {
    actions.push({ tone: "neutral", title: "继续积累样本", detail: "当前记录不足以做强结论，先保持采购节奏并完成真实销售记录。" });
  }
  return actions.slice(0, 3);
}

function LedgerAnalytics({
  ledger,
  period,
  onPeriodChange
}: {
  ledger: AdminTradeLedger;
  period: TradeLedgerPeriod;
  onPeriodChange: (period: TradeLedgerPeriod) => void;
}) {
  const skuRows = buildAnalyticsSkuRows(ledger);
  const rows = ledger.analytics?.[period] ?? [];
  const actions = buildAnalyticsActions(skuRows);
  const inventoryValue = skuRows.reduce((sum, row) => sum + row.inventoryValue, 0);
  const inventoryUnits = skuRows.reduce((sum, row) => sum + row.inventoryUnits, 0);
  const categoryRows = Array.from(skuRows.reduce((groups, row) => {
    const key = row.subcategory || categoryLabel(row.category);
    const existing = groups.get(key) ?? { key, inventoryUnits: 0, inventoryValue: 0, soldUnits: 0, margin: 0, samples: 0 };
    existing.inventoryUnits += row.inventoryUnits;
    existing.inventoryValue += row.inventoryValue;
    existing.soldUnits += row.soldUnits;
    existing.margin += row.realizedMargin;
    existing.samples += row.sampleCount;
    groups.set(key, existing);
    return groups;
  }, new Map<string, { key: string; inventoryUnits: number; inventoryValue: number; soldUnits: number; margin: number; samples: number }>()).values())
    .sort((a, b) => b.inventoryValue - a.inventoryValue);

  return (
    <section className="ledger-analysis analytics-dashboard" aria-label="经营分析">
      <div className="analytics-summary-strip" aria-label="经营温度">
        <span><small>已实现毛利</small><strong className={ledger.summary.realizedMargin >= 0 ? "positive" : "negative"}>{currency.format(ledger.summary.realizedMargin)}</strong></span>
        <span><small>库存占用</small><strong>{currency.format(inventoryValue)}</strong></span>
        <span><small>在库数量</small><strong>{inventoryUnits} 件</strong></span>
        <span><small>平均周转</small><strong>{formatDays(ledger.summary.averageSalesCycleDays)}</strong></span>
      </div>

      <section className="analytics-action-panel" aria-label="今日动作">
        <header><h4>经营提醒</h4></header>
        <div className="analytics-action-list">
          {actions.map((action) => (
            <div className={`analytics-action-item ${action.tone}`} key={`${action.title}-${action.detail}`}>
              <i aria-hidden="true" />
              <div><strong>{action.title}</strong><span>{action.detail}</span></div>
            </div>
          ))}
        </div>
      </section>

      <section className="analytics-sku-panel" aria-label="SKU经营表">
        <header><h4>商品表现</h4><small>{skuRows.length} 个商品</small></header>
        {skuRows.length === 0 ? <p className="ledger-empty">暂无真实商品样本。</p> : (
          <div className="analytics-sku-table" role="table" aria-label="SKU经营数据">
            <div className="analytics-sku-row analytics-sku-head" role="row"><span>商品</span><span className="analytics-sku-value">库存</span><span className="analytics-sku-value">毛利</span><span className="analytics-sku-value">库龄</span><span>判断</span></div>
            {skuRows.map((row) => {
              const status = analyticsSkuStatus(row);
              const marginRate = row.netRevenue > 0 ? `${(row.realizedMargin / row.netRevenue * 100).toFixed(1)}%` : "—";
              return (
                <div className="analytics-sku-row" role="row" key={row.key}>
                  <span className="analytics-sku-title"><strong>{row.title}</strong><small>{row.subcategory} · {row.soldUnits} 售 / {row.sampleCount} 样本</small></span>
                  <span className="analytics-sku-value">{row.inventoryUnits} 件</span>
                  <span className="analytics-sku-value">{marginRate}</span>
                  <span className="analytics-sku-value">{row.oldestInventoryDays === null ? "—" : `${row.oldestInventoryDays} 天`}</span>
                  <span><em className={`analytics-status ${status.tone}`}>{status.label}</em></span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="analytics-category-panel" aria-label="品类经营">
        <header><h4>品类表现</h4></header>
        {categoryRows.length === 0 ? <p className="ledger-empty">暂无真实品类样本。</p> : (
          <div className="analytics-category-list">
            {categoryRows.map((row) => (
              <div className="analytics-category-row" key={row.key}>
                <strong>{row.key}</strong>
                <span>库存 {row.inventoryUnits} 件 · 占用 {currency.format(row.inventoryValue)}</span>
                <span>{row.soldUnits > 0 ? `已售 ${row.soldUnits} 件` : "尚无成交样本"}</span>
                <em className={row.samples < 3 ? "neutral" : row.soldUnits === 0 ? "neutral" : row.margin >= 0 ? "positive" : "warn"}>{row.samples < 3 ? "样本不足" : row.soldUnits === 0 ? "暂无成交" : row.margin >= 0 ? "维持观察" : "控制采购"}</em>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="analytics-trend-panel" aria-label="周期趋势">
        <div className="analytics-period-bar">
          <div className="analytics-trend-title"><h4>收支趋势</h4></div>
          <div className="ledger-period-tabs" role="tablist" aria-label="分析周期">
            {periodTabs.map((item) => (
              <button className={period === item.id ? "active" : ""} type="button" role="tab" aria-selected={period === item.id} key={item.id} onClick={() => onPeriodChange(item.id)}>{item.label}</button>
            ))}
          </div>
        </div>
        {rows.length === 0 ? <p className="ledger-empty">暂无足够的真实记录生成趋势。</p> : <LedgerTrendChart rows={rows} />}
      </section>
    </section>
  );
}

function LedgerTrendChart({ rows }: { rows: AdminTradeLedgerPeriodSummary[] }) {
  const points = rows.slice(0, 8).reverse();
  const scale = Math.max(
    1,
    ...points.flatMap((row) => [Number(row.netRevenue), Number(row.expense)])
  );
  const width = 720;
  const height = 280;
  const plotLeft = 58;
  const plotTop = 68;
  const plotWidth = 638;
  const plotHeight = 134;
  const baseline = plotTop + plotHeight;
  const groupWidth = plotWidth / Math.max(points.length, 1);
  const barWidth = Math.min(22, groupWidth * 0.22);
  const barGap = Math.min(7, groupWidth * 0.08);
  const ticks = [scale, scale * 0.5, 0];

  return (
    <div className="ledger-trend-chart" aria-label="收入、支出和毛利趋势">
      <div className="ledger-trend-scroll" role="region" aria-label="收支趋势，可左右滚动查看周期" tabIndex={0}>
      <svg className="ledger-trend-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="ledger-trend-title ledger-trend-desc">
        <title id="ledger-trend-title">收入与支出趋势</title>
        <desc id="ledger-trend-desc">每个周期用绿色柱表示收入，琥珀色柱表示支出，底部标注毛利。</desc>
        <g className="ledger-trend-svg-legend" aria-hidden="true">
          <circle cx="60" cy="17" r="4" className="income" />
          <text x="70" y="21">收入</text>
          <circle cx="122" cy="17" r="4" className="outflow" />
          <text x="132" y="21">支出</text>
        </g>
        <g className="ledger-trend-svg-axis" aria-hidden="true">
          {ticks.map((tick, index) => {
            const y = plotTop + (plotHeight * index) / (ticks.length - 1);
            return (
              <g key={`tick-${tick}`}>
                <line x1={plotLeft} x2={plotLeft + plotWidth} y1={y} y2={y} />
                <text x={plotLeft - 9} y={y + 4} textAnchor="end">{formatTrendAmount(tick)}</text>
              </g>
            );
          })}
        </g>
        {points.map((row, index) => {
          const center = plotLeft + groupWidth * index + groupWidth / 2;
          const incomeHeight = Math.max(0, Number(row.netRevenue) / scale * plotHeight);
          const expenseHeight = Math.max(0, Number(row.expense) / scale * plotHeight);
          const incomeX = center - barWidth - barGap / 2;
          const expenseX = center + barGap / 2;
          const incomeLabelY = baseline - incomeHeight - 7;
          const expenseLabelY = baseline - expenseHeight - 7;
          const separatedExpenseLabelY = Math.abs(incomeLabelY - expenseLabelY) < 18 ? expenseLabelY - 18 : expenseLabelY;
          return (
            <g className="ledger-trend-svg-group" key={`${row.period}-${row.key}`}>
              <title>{`${row.label}：收入 ${currency.format(row.netRevenue)}，支出 ${currency.format(row.expense)}，毛利 ${currency.format(row.realizedMargin)}`}</title>
              <rect className="income" x={incomeX} y={baseline - incomeHeight} width={barWidth} height={incomeHeight} rx="4" />
              <rect className="outflow" x={expenseX} y={baseline - expenseHeight} width={barWidth} height={expenseHeight} rx="4" />
              <text className="ledger-trend-svg-value income" x={incomeX + barWidth / 2} y={incomeLabelY} textAnchor="middle">{formatTrendAmount(row.netRevenue)}</text>
              <text className="ledger-trend-svg-value outflow" x={expenseX + barWidth / 2} y={separatedExpenseLabelY} textAnchor="middle">{formatTrendAmount(row.expense)}</text>
              <text className="ledger-trend-svg-period" x={center} y={baseline + 19} textAnchor="middle">{row.label}</text>
              <text className={`ledger-trend-svg-margin ${row.realizedMargin >= 0 ? "positive" : "negative"}`} x={center} y={baseline + 35} textAnchor="middle">毛利 {formatTrendAmount(row.realizedMargin)}</text>
            </g>
          );
        })}
      </svg>
      </div>
      <p className="ledger-trend-scroll-hint">左右滑动查看完整周期</p>
    </div>
  );
}

function PeriodCard({ row }: { row: AdminTradeLedgerPeriodSummary }) {
  return (
    <article className="ledger-analysis-card">
      <header>
        <span>{row.label}</span>
        <strong className={row.realizedMargin >= 0 ? "positive" : "negative"}>
          {currency.format(row.realizedMargin)}
        </strong>
      </header>
      <div className="ledger-analysis-facts">
        <span>
          <small>收入</small>
          {currency.format(row.netRevenue)}
        </span>
        <span>
          <small>采购与整备</small>
          {currency.format(row.buyCost + row.refurbishmentCost)}
        </span>
        <span>
          <small>销售费用</small>
          {currency.format(row.sellingExpense)}
        </span>
        <span>
          <small>周期</small>
          {formatDays(row.averageSalesCycleDays)}
        </span>
      </div>
      <footer>
        采 {row.procurementCount} 笔 · 售 {row.soldQuantity} 件 · 在架 {row.listedQuantity} 件
      </footer>
    </article>
  );
}

function LotPreview({ lot }: { lot: AdminTradeProcurement }) {
  const remaining = Number(lot.quantity) - Number(lot.sold_quantity);
  return (
    <div className="ledger-preview">
      <div className="ledger-preview-head">
        <span>采购 #{lot.id} · {translateTradePlatform(String(lot.platform))}</span>
        <strong>{lot.title}</strong>
      </div>
      <div className="ledger-preview-facts">
        <span><small>可售</small>{remaining}/{lot.quantity} 件</span>
        <span><small>单位成本</small>{currency.format(procurementUnitCost(lot))}</span>
        <span><small>采购时间</small>{formatProcurementDateTime(lot.purchased_at)}</span>
      </div>
      <small>{categoryLabel(lot.category)} · {lot.subcategory || "未分子类"} · {lot.color || "未记颜色"}</small>
      {lot.gift_info && <small>赠品 {lot.gift_info}</small>}
    </div>
  );
}

function LedgerHistory({
  ledger,
  updatingSaleId,
  onSaleStatusChange,
  onEditProcurement
}: {
  ledger: AdminTradeLedger | null;
  updatingSaleId: number | null;
  onSaleStatusChange: (sale: AdminTradeSale, status: "sold" | "cancelled") => Promise<void>;
  onEditProcurement: (item: AdminTradeProcurement) => void;
}) {
  const [filter, setFilter] = useState<"all" | "buy" | "listed" | "sold" | "cancelled">("all");
  const [query, setQuery] = useState("");
  const [pendingCancelId, setPendingCancelId] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(5);

  if (!ledger || (ledger.sales.length === 0 && ledger.procurements.length === 0)) {
    return <p className="ledger-empty">还没有台账。先记一笔站外采购。</p>;
  }

  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
  const matchingEntries = [
    ...ledger.sales.map((sale) => ({
      kind: "sale" as const,
      record: sale,
      sortAt: timeValue(sale.sold_at ?? sale.listed_at ?? sale.created_at)
    })),
    ...ledger.procurements.map((item) => ({
      kind: "buy" as const,
      record: item,
      sortAt: timeValue(item.purchased_at ?? item.created_at)
    }))
  ]
    .filter((entry) => {
      if (filter === "buy" && entry.kind !== "buy") {
        return false;
      }
      if (filter !== "all" && filter !== "buy" && (entry.kind !== "sale" || entry.record.status !== filter)) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      const record = entry.record;
      return [record.id, record.title, record.platform, record.external_ref]
        .some((value) => String(value ?? "").toLocaleLowerCase("zh-CN").includes(normalizedQuery));
    })
    .sort((a, b) => b.sortAt - a.sortAt);
  const entries = matchingEntries.slice(0, visibleCount);

  return (
    <section className="ledger-history-section" aria-label="交易记录">
      <div className="ledger-history-head">
        <div>
          <p className="eyebrow">最近记录</p>
          <h4>采购与销售</h4>
        </div>
        <label className="ledger-search">
          <Search size={15} aria-hidden="true" />
          <span className="sr-only">搜索交易记录</span>
          <input value={query} onChange={(event) => {
            setQuery(event.target.value);
            setVisibleCount(5);
          }} placeholder="商品、单号" />
        </label>
      </div>
      <div className="ledger-filter-tabs" role="group" aria-label="筛选交易记录">
        {([
          ["all", "全部"],
          ["buy", "采购"],
          ["listed", "在架"],
          ["sold", "已售"],
          ["cancelled", "已取消"]
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={filter === id ? "active" : ""}
            aria-pressed={filter === id}
            onClick={() => {
              setFilter(id);
              setVisibleCount(5);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {entries.length === 0 ? (
        <p className="ledger-empty">没有符合条件的记录。</p>
      ) : (
        <div className="ledger-history">
          {entries.map((entry) =>
            entry.kind === "sale" ? (
              <SaleHistoryCard
                sale={entry.record}
                busy={updatingSaleId === entry.record.id}
                pendingCancel={pendingCancelId === entry.record.id}
                onMarkSold={() => onSaleStatusChange(entry.record, "sold")}
                onAskCancel={() => setPendingCancelId(entry.record.id)}
                onCancel={() => {
                  setPendingCancelId(null);
                  void onSaleStatusChange(entry.record, "cancelled");
                }}
                onKeep={() => setPendingCancelId(null)}
                key={`sale-${entry.record.id}`}
              />
            ) : (
              <ProcurementHistoryCard item={entry.record} onEdit={onEditProcurement} key={`buy-${entry.record.id}`} />
            )
          )}
        </div>
      )}
      {matchingEntries.length > entries.length && (
        <button className="ledger-load-more" type="button" onClick={() => setVisibleCount((count) => count + 5)}>
          查看更多 · 还有 {matchingEntries.length - entries.length} 条
        </button>
      )}
    </section>
  );
}

function SaleHistoryCard({
  sale,
  busy,
  pendingCancel,
  onMarkSold,
  onAskCancel,
  onCancel,
  onKeep
}: {
  sale: AdminTradeSale;
  busy: boolean;
  pendingCancel: boolean;
  onMarkSold: () => void;
  onAskCancel: () => void;
  onCancel: () => void;
  onKeep: () => void;
}) {
  const feeTotal = Number(sale.outbound_freight) + Number(sale.consumable_cost) + Number(sale.platform_fee) + Number(sale.misc_cost);
  return (
    <article className={`ledger-row sale ${tradeCategoryClass(sale.category, sale.procurement_subcategory)}`}>
      <div>
        <p className="ledger-kicker">
          {translateTradePlatform(String(sale.platform))}售卖 · 采 #{sale.procurement_order_id}
        </p>
        <h4>{sale.title}</h4>
        <div className="ledger-linked-procurement">
          <span>采购来源：{sale.procurement_title}</span>
          <span>{translateTradePlatform(String(sale.buy_platform))} · {formatProcurementDate(sale.purchased_at)}</span>
          <span>采购成本 {currency.format(procurementUnitCost({
            quantity: sale.buy_quantity,
            buy_price: sale.buy_price,
            inbound_freight: sale.inbound_freight,
            consumable_cost: sale.buy_consumable_cost,
            misc_cost: sale.buy_misc_cost,
            refurbishment_cost: sale.buy_refurbishment_cost
          }))}/件 · 批次 {sale.buy_quantity} 件</span>
        </div>
        <div className="ledger-tags">
          <span>{categoryLabel(sale.category)}</span>
          <span>{shippingMethodLabel(sale.shipping_method)}</span>
          {sale.color && <span>{sale.color}</span>}
          {sale.gift_info && <span>赠 {sale.gift_info}</span>}
          {sale.external_ref && <span>{sale.external_ref}</span>}
        </div>
        <div className="ledger-facts">
          <span>
            <small>售价</small>
            {currency.format(Number(sale.sale_price))}
          </span>
          <span>
            <small>净收</small>
            {currency.format(saleNetAmount(sale))}
          </span>
          <span>
            <small>费用</small>
            {currency.format(feeTotal)}
          </span>
          <span>
            <small>毛利</small>
            {currency.format(sale.margin)}
          </span>
        </div>
      </div>
      <div className="ledger-row-side">
        <em className={`ledger-status ${sale.status}`}>{formatResaleStatus(sale.status)}</em>
        <small>{sale.status === "sold" ? formatProcurementDate(sale.sold_at) : formatProcurementDate(sale.listed_at)}</small>
        {sale.status === "listed" && !pendingCancel && (
          <div className="ledger-row-actions">
            <button type="button" className="ledger-action-primary" disabled={busy} onClick={onMarkSold}>
              <CircleCheck size={14} />
              {busy ? "处理中" : "确认成交"}
            </button>
            <button type="button" disabled={busy} onClick={onAskCancel}>取消上架</button>
          </div>
        )}
        {sale.status === "listed" && pendingCancel && (
          <div className="ledger-cancel-confirm" role="alert">
            <span>取消后将释放库存</span>
            <div>
              <button type="button" className="danger" onClick={onCancel}><Ban size={13} />确认取消</button>
              <button type="button" onClick={onKeep}>保留</button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function formatResaleStatus(status: AdminTradeSale["status"]) {
  return {
    listed: "在架",
    sold: "已成交",
    cancelled: "已取消"
  }[status];
}

function ProcurementHistoryCard({ item, onEdit }: { item: AdminTradeProcurement; onEdit: (item: AdminTradeProcurement) => void }) {
  const remaining = Number(item.quantity) - Number(item.sold_quantity);
  return (
    <article className={`ledger-row buy ${tradeCategoryClass(item.category, item.subcategory)}`}>
      <div>
        <p className="ledger-kicker">
          {translateTradePlatform(String(item.platform))}采购 · #{item.id}
        </p>
        <h4>{item.title}</h4>
        <div className="ledger-tags">
          <span>{categoryLabel(item.category)}</span>
          <span>{shippingMethodLabel(item.shipping_method)}</span>
          {item.subcategory && <span>{item.subcategory}</span>}
          {item.color && <span>{item.color}</span>}
          {item.gift_info && <span>赠 {item.gift_info}</span>}
          {item.external_ref && <span>{item.external_ref}</span>}
        </div>
        <div className="ledger-facts">
          <span>
            <small>进价</small>
            {currency.format(Number(item.buy_price))}
          </span>
          <span>
            <small>落地成本</small>
            {currency.format(procurementTotalCost(item))}
          </span>
          <span>
            <small>整备成本</small>
            {currency.format(Number(item.refurbishment_cost))}
          </span>
          <span>
            <small>库存</small>
            余 {remaining}/{item.quantity}
          </span>
        </div>
      </div>
      <div className="ledger-row-side">
        <em>{formatProcurementDate(item.purchased_at)}</em>
        {remaining <= 0 && <small>出完</small>}
        {Number(item.sold_quantity) === 0 && (
          <button className="ledger-edit-button" type="button" onClick={() => onEdit(item)}>编辑</button>
        )}
      </div>
    </article>
  );
}

function PlatformPicker({
  value,
  onChange
}: {
  value: TradePlatform;
  onChange: (value: TradePlatform) => void;
}) {
  return (
    <label>
      <span>平台</span>
      <select value={value} onChange={(event) => onChange(event.target.value as TradePlatform)}>
        {tradePlatforms.map((platform) => <option value={platform.id} key={platform.id}>{platform.label}</option>)}
      </select>
    </label>
  );
}
